import React, { createContext, useContext, useState, useCallback } from 'react';
import { CheckCircleIcon, XCircleIcon, ExclamationTriangleIcon, InformationCircleIcon, XMarkIcon } from '@heroicons/react/24/outline';

const ToastContext = createContext(null);

let toastId = 0;

export const ToastProvider = ({ children }) => {
    const [toasts, setToasts] = useState([]);

    const addToast = useCallback((message, type = 'success', duration = 4000, onClick = null) => {
        let createdId = null;
        setToasts(prev => {
            // Aynı mesaj zaten ekranda varsa mükerrer toast ekleme
            if (prev.some(t => t.message === message)) {
                return prev;
            }
            createdId = ++toastId;
            const updated = [...prev, { id: createdId, message, type, onClick }];
            return updated.slice(-3); // Maksimum 3 toast göster
        });

        if (createdId) {
            const currentId = createdId;
            setTimeout(() => {
                setToasts(prev => prev.filter(t => t.id !== currentId));
            }, duration);
        }
        return createdId;
    }, []);

    const removeToast = useCallback((id) => {
        setToasts(prev => prev.filter(t => t.id !== id));
    }, []);

    const toast = {
        success: (msg, duration, onClick) => addToast(msg, 'success', duration, onClick),
        error: (msg, duration, onClick) => addToast(msg, 'error', duration, onClick),
        warning: (msg, duration, onClick) => addToast(msg, 'warning', duration, onClick),
        info: (msg, duration, onClick) => addToast(msg, 'info', duration, onClick),
        whatsapp: (msg, duration, onClick) => addToast(msg, 'whatsapp', duration, onClick),
        showSuccess: (msg, duration, onClick) => addToast(msg, 'success', duration, onClick),
        showError: (msg, duration, onClick) => addToast(msg, 'error', duration, onClick),
        showWarning: (msg, duration, onClick) => addToast(msg, 'warning', duration, onClick),
        showInfo: (msg, duration, onClick) => addToast(msg, 'info', duration, onClick),
    };

    const icons = {
        success: <CheckCircleIcon className="h-5 w-5 text-green-500 flex-shrink-0" />,
        error: <XCircleIcon className="h-5 w-5 text-red-500 flex-shrink-0" />,
        warning: <ExclamationTriangleIcon className="h-5 w-5 text-yellow-500 flex-shrink-0" />,
        info: <InformationCircleIcon className="h-5 w-5 text-blue-500 flex-shrink-0" />,
        whatsapp: (
            <div className="w-8 h-8 rounded-full bg-[#25D366] flex items-center justify-center flex-shrink-0 shadow-md">
                <svg className="w-5 h-5 fill-white" viewBox="0 0 24 24">
                    <path d="M12.031 6.172c-3.181 0-5.767 2.586-5.768 5.766-.001 1.298.38 2.27 1.019 3.287l-.582 2.128 2.182-.573c.978.58 1.911.928 3.145.929 3.178 0 5.767-2.587 5.768-5.766.001-3.187-2.575-5.771-5.764-5.771zm3.392 8.244c-.144.405-.837.774-1.17.824-.299.045-.677.063-1.092-.069-.252-.08-.575-.187-.988-.365-1.739-.751-2.874-2.502-2.961-2.617-.087-.116-.708-.94-.708-1.793s.448-1.273.607-1.446c.159-.173.346-.217.462-.217l.332.006c.106.005.249-.04.39.298.144.347.491 1.2.534 1.287.043.087.072.188.014.304-.058.116-.087.188-.173.289l-.26.304c-.087.086-.177.18-.076.354.101.174.449.741.964 1.201.662.591 1.221.774 1.394.86s.275.072.376-.043c.101-.116.433-.506.549-.68.116-.173.231-.145.39-.087s1.011.477 1.184.564.289.13.332.202c.045.072.045.419-.1.824zm-3.423-14.416c-6.627 0-12 5.373-12 12 0 2.159.579 4.184 1.594 5.926l-1.602 5.856 5.992-1.572c1.701.928 3.652 1.457 5.727 1.457 6.627 0 12-5.373 12-12 0-6.627-5.373-12-12-12z" />
                </svg>
            </div>
        )
    };

    const bgColors = {
        success: 'bg-white border-l-4 border-green-500',
        error: 'bg-white border-l-4 border-red-500',
        warning: 'bg-white border-l-4 border-yellow-400',
        info: 'bg-white border-l-4 border-blue-500',
        whatsapp: 'bg-[#ffffff] dark:bg-[#111b21] border-l-4 border-[#25D366] shadow-[0_8px_30px_rgb(0,0,0,0.12)] ring-1 ring-black/5',
    };

    return (
        <ToastContext.Provider value={toast}>
            {children}
            {/* Toast Container — fixed sağ alt */}
            <div className="fixed bottom-6 right-6 z-[999999] flex flex-col space-y-3 max-w-sm w-full pointer-events-none">
                {toasts.map(t => (
                    <div
                        key={t.id}
                        onClick={() => {
                            if (t.onClick) {
                                t.onClick();
                                removeToast(t.id);
                            }
                        }}
                        className={`pointer-events-auto flex items-start gap-3 px-4 py-3 rounded-xl shadow-2xl ${bgColors[t.type]} ${t.onClick ? 'cursor-pointer hover:shadow-indigo-500/10 hover:scale-[1.02] transition-transform' : ''} animate-slide-in`}
                        style={{ animation: 'slideInRight 0.3s ease-out' }}
                    >
                        {icons[t.type]}
                        <div className="flex-1 min-w-0">
                            <p className={`text-sm font-medium ${t.type === 'whatsapp' ? 'text-gray-900 dark:text-[#e9edef]' : 'text-gray-800'}`}>
                                {t.message}
                            </p>
                            {t.onClick && (
                                <span className={`text-[10px] font-bold block mt-0.5 ${t.type === 'whatsapp' ? 'text-[#00a884] dark:text-[#25D366]' : 'text-indigo-600'}`}>
                                    Sohbete gitmek için tıklayın →
                                </span>
                            )}
                        </div>
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                removeToast(t.id);
                            }}
                            className="text-gray-400 hover:text-gray-600 flex-shrink-0 p-1"
                        >
                            <XMarkIcon className="h-4 w-4" />
                        </button>
                    </div>
                ))}
            </div>
            <style>{`
                @keyframes slideInRight {
                    from { transform: translateX(100%); opacity: 0; }
                    to   { transform: translateX(0);    opacity: 1; }
                }
            `}</style>
        </ToastContext.Provider>
    );
};

export const useToast = () => {
    const ctx = useContext(ToastContext);
    if (!ctx) throw new Error('useToast must be used within ToastProvider');
    return ctx;
};
